import type { GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging.ts";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile.ts";
import { wait } from "@entrilia/shared/utilities/promiseHelper.ts";
import biApi from "../../../../../../api/biApi.ts";
import type { BuildApArAgingDetailReportRequest, SortConfiguration } from "../../../../../../api/types/svTypes.ts";
import { Ordering, SortType } from "../../../../../../api/types/svTypes.ts";
import type { AgingDetailColumn } from "../types/agingDetailTypes.ts";

const maxExportReadyAttempts = 200;
const exportReadyPollDelayMs = 1500;

const convertSortModelToSortConfig = (sortModel: GridSortModel, columns: AgingDetailColumn[]): SortConfiguration[] => {
  const columnsByGuid = new Map(columns.map((col) => [col.guid, col]));
  return sortModel.flatMap((item) => {
    const col = columnsByGuid.get(item.field);
    if (!col) return [];
    return {
      name: col.name,
      id: col.guid,
      ordering: item.sort === "asc" ? Ordering.Ascending : Ordering.Descending,
      caption: undefined,
      type: SortType.Dimension,
    };
  });
};

export const useAgingDetailExport = (
  request: BuildApArAgingDetailReportRequest,
  sortModel: GridSortModel,
  columns: AgingDetailColumn[],
  exportValuesAsDisplayed: boolean
) => {
  const [exporting, setExporting] = useState(false);
  const { sendNotificationError } = useNotificationContext();

  const downloadExport = useCallback(async (token: string) => {
    await downloadFileFromUrl(biApi.generateExportDownloadLink(token));
  }, []);

  const waitExportReady = useCallback(
    async (token: string): Promise<void> => {
      try {
        for (let attempt = 0; attempt <= maxExportReadyAttempts; attempt += 1) {
          const response = await biApi.checkExportReady(token);
          if (!response.success || response.data.error) {
            sendNotificationError("An error occurred while exporting the file. Please try again.");
            setExporting(false);
            return;
          }

          if (response.data.fileProcessed) {
            await downloadExport(token);
            setExporting(false);
            return;
          }

          if (attempt < maxExportReadyAttempts) {
            await wait(exportReadyPollDelayMs);
          }
        }
        sendNotificationError("An error occurred while generating an export file. Please try again.");
        setExporting(false);
      } catch (error) {
        logError(error, "[useAgingDetailExport] checkExportReady");
        sendNotificationError("An error occurred while exporting the file. Please try again.");
        setExporting(false);
      }
    },
    [downloadExport, sendNotificationError]
  );

  const requestExport = useCallback(
    (withParameters: boolean) => {
      setExporting(true);
      const sort = convertSortModelToSortConfig(sortModel, columns);
      biApi
        .requestBuildApArAgingDetailExport({
          buildConfig: { ...request, sort },
          exportConfig: { withParameters, parameters: [], exportValuesAsDisplayed },
        })
        .then((response) => {
          if (!response.success) {
            sendNotificationError("An error occurred while exporting the file. Please try again.");
            setExporting(false);
            return;
          }

          void waitExportReady(response.data.token);
        })
        .catch(() => {
          sendNotificationError("An error occurred while exporting the file. Please try again.");
          setExporting(false);
        });
    },
    [request, sortModel, columns, exportValuesAsDisplayed, sendNotificationError, waitExportReady]
  );

  return {
    exporting,
    requestExport,
    requestExportWithParameters: () => requestExport(true),
    requestExportWithoutParameters: () => requestExport(false),
  };
};
